"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, MessageSquare, PhoneCall, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const features = [{
  title: "Tailored Method Evaluation",
  description: "Assess sample viability and choose optimal spectroscopic or physical testing techniques."
}, {
  title: "Feasibility & Trial Support",
  description: "Proof-of-concept testing in lab and process environments before procurement."
}, {
  title: "Compliance & Qualification",
  description: "Direct alignment with regulated GMP requirements across Ireland and the UK."
}];
export default function DiscussCTA() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="discussapplicationcta" data-nav="light" className="relative bg-background py-16 md:py-24 border-t border-border overflow-hidden">
      <div className="page-container relative z-10">
        <div className="relative overflow-hidden rounded-2xl border border-border bg-secondary/50 p-8 md:p-12 lg:p-16 shadow-lg">
          {/* Subtle Decorative Background Elements */}
          <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-chart-2/10 blur-3xl" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Left Content Column */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="lg:col-span-7 flex flex-col gap-5">
              <Badge variant="secondary" className="w-fit gap-1.5 px-3 py-1 font-medium bg-background border border-border text-foreground">
                <Sparkles className="size-3.5 text-primary" />
                Expert Analytical Support
              </Badge>

              <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.15]">
                Ready to optimize your testing and process workflow?
              </h2>

              <p className="font-default text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
                Connect directly with our technical specialists. We help regulated laboratories and manufacturing teams select, qualify, and maintain the right analytical instrumentation.
              </p>

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                  <Link to="/DiscussYourApplicationPage" className="inline-flex items-center gap-2">
                    <MessageSquare className="size-4" />
                    Discuss Your Application
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-muted font-medium">
                  <Link to="/ContactPage" className="inline-flex items-center gap-2">
                    <PhoneCall className="size-4" />
                    Speak with an Engineer
                  </Link>
                </Button>
              </div>
            </motion.div>

            {/* Right Feature Card Column */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1,
            ease: "easeOut"
          }} className="lg:col-span-5">
              <div className="rounded-xl border border-border bg-card p-6 md:p-8 shadow-sm flex flex-col gap-5">
                <h3 className="font-default text-lg font-semibold text-card-foreground">
                  What you get with our application consultation:
                </h3>

                <div className="space-y-4">
                  {features.map((feature, i) => <div data-index={i} key={i} className="flex items-start gap-3.5">
                      <div className="mt-0.5 rounded-full bg-primary/10 p-1 text-primary shrink-0">
                        <CheckCircle2 className="size-4" />
                      </div>
                      <div className="space-y-0.5">
                        <p className="font-default text-sm font-semibold text-card-foreground">
                          {feature.title}
                        </p>
                        <p className="font-default text-xs md:text-sm text-muted-foreground leading-snug">
                          {feature.description}
                        </p>
                      </div>
                    </div>)}
                </div>

                <div className="pt-2 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                  <span>Direct Ireland &amp; UK support</span>
                  <Link to="/ApplicationConsultationPage" className="text-primary hover:underline font-medium">
                    Explore consultation
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}